import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import TextField from '@mui/material/TextField'
import {
  type GridColDef,
  type GridFilterInputValueProps,
  type GridFilterItem,
  type GridFilterOperator,
  type GridRenderCellParams,
  getGridDateOperators,
  getGridStringOperators,
} from '@mui/x-data-grid-premium'
import type { AuditValue } from '@mynt/backoffice-api-spec/card-audit/models'
import { AuditValueType, CardAuditAction } from '@mynt/backoffice-api-spec/card-audit/models'
import { useRef, useState } from 'react'
import { DataGridCellText } from './DataGridCellText'

const EXPAND_THRESHOLD = 5

const ExpandableListCell = ({ items }: { items: string[] }) => {
  const [expanded, setExpanded] = useState(false)
  const visibleItems = expanded ? items : items.slice(0, EXPAND_THRESHOLD)
  const hiddenCount = items.length - EXPAND_THRESHOLD

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, py: 1 }}>
      <DataGridCellText py={0}>{visibleItems.join(', ')}</DataGridCellText>
      <Button
        variant="text"
        size="small"
        onClick={() => setExpanded((prev) => !prev)}
        sx={{
          p: 0,
          minWidth: 0,
          textTransform: 'none',
          fontSize: '0.8125rem',
          fontWeight: 400,
          color: 'primary.main',
          lineHeight: 1.5,
          '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
        }}
      >
        {expanded ? 'Show less' : `Show ${hiddenCount} more`}
      </Button>
    </Box>
  )
}

export type CardAuditLog = {
  id: string
  cardAuditType: CardAuditAction
  values: AuditValue
  when: string
  who: string
  shownToCustomer: boolean
}

const renderAuditValueCell = (auditValue: AuditValue, key: 'oldValue' | 'newValue') => {
  if (!auditValue) {
    return <DataGridCellText>-</DataGridCellText>
  }

  const val = (auditValue as any)[key]
  if (val == null || val === '') {
    return <DataGridCellText>-</DataGridCellText>
  }

  switch (auditValue.type) {
    case AuditValueType.DECIMAL:
      return <DataGridCellText>{Intl.NumberFormat('sv-SE', { minimumFractionDigits: 0 }).format(val as number)}</DataGridCellText>

    case AuditValueType.BOOLEAN:
      return <DataGridCellText>{val ? 'Yes' : 'No'}</DataGridCellText>

    case AuditValueType.ADDRESS:
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', py: 1 }}>
          {Object.entries(val).map(([k, v]) => (
            <DataGridCellText key={k} py={0}>
              <strong>{formatSnakeCaseToSentence(k)}: </strong> {String(v)}
            </DataGridCellText>
          ))}
        </Box>
      )
    case AuditValueType.CARD_HOLDER:
      return <DataGridCellText>{val?.fullName || '-'}</DataGridCellText>

    case AuditValueType.SPEND_CONTROL_CATEGORIES:
    case AuditValueType.SPEND_CONTROL_COUNTRY_GROUPS:
      if ((val as string[]).length === 0) {
        return <DataGridCellText>-</DataGridCellText>
      }
      if ((val as string[]).length === 1) {
        return <DataGridCellText>{(val as string[])[0]}</DataGridCellText>
      }
      if ((val as string[]).length > EXPAND_THRESHOLD) {
        return <ExpandableListCell items={val as string[]} />
      }
      if ((val as string[]).length >= 4) {
        return <DataGridCellText>{(val as string[]).join(', ')}</DataGridCellText>
      }
      return (
        <Box component="ul" sx={{ pl: 2, my: 0.5 }}>
          {(val as string[]).map((item) => (
            <Box component="li" key={item}>
              <DataGridCellText py={0}>{item}</DataGridCellText>
            </Box>
          ))}
        </Box>
      )

    case AuditValueType.ACTIVE_STATUS:
    case AuditValueType.CARD_CATEGORY:
    case AuditValueType.CARD_GROUP:
    case AuditValueType.CARD_STATUS:
    case AuditValueType.PERSON_ID:
    case AuditValueType.STRING:
    default:
      return <DataGridCellText>{String(val)}</DataGridCellText>
  }
}

const getAuditValueString = (auditValue: AuditValue, key: 'oldValue' | 'newValue'): string => {
  if (!auditValue) return ''

  const val = (auditValue as any)[key]
  if (val == null || val === '') return ''

  switch (auditValue.type) {
    case AuditValueType.DECIMAL:
      return val
    case AuditValueType.BOOLEAN:
      return val ? 'Yes' : 'No'
    case AuditValueType.ADDRESS:
      return Object.entries(val)
        .map(([k, v]) => `${formatSnakeCaseToSentence(k)}: ${v}`)
        .join(', ')
    case AuditValueType.CARD_HOLDER:
      return val?.fullName || ''
    case AuditValueType.SPEND_CONTROL_CATEGORIES:
    case AuditValueType.SPEND_CONTROL_COUNTRY_GROUPS:
      return (val as string[]).join(', ')
    default:
      return String(val)
  }
}

const formatSnakeCaseToSentence = (str?: string) => {
  if (!str) return ''

  const cleanString = str.replace(/_/g, ' ').toLowerCase()

  return cleanString.charAt(0).toUpperCase() + cleanString.slice(1)
}

const parseAuditTimestamp = (when: string | null | undefined): Date | null => {
  if (!when) return null

  const date = new Date(when)
  return isNaN(date.getTime()) ? null : date
}

const formatAuditDate = (date: Date | null | undefined): string => {
  if (!date) return '-'

  return new Intl.DateTimeFormat('sv-SE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(date)
}

const createSelectOperator = (
  label: string,
  options: string[],
  getOptionLabel?: (option: string) => string,
  mapValue?: (option: string) => any,
): GridFilterOperator => {
  const sortedOptions = [...options].sort((a, b) => {
    const labelA = getOptionLabel ? getOptionLabel(a) : a
    const labelB = getOptionLabel ? getOptionLabel(b) : b
    return labelA.localeCompare(labelB)
  })

  return {
    label: label,
    value: 'customArrayContains',
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      if (!filterItem.value || filterItem.value.length === 0) return null

      const filterValues = mapValue ? filterItem.value.map(mapValue) : Array.from(filterItem.value)

      return (value: unknown) => filterValues.includes(value)
    },
    InputComponent: ({ applyValue, clearButton, ...params }) => {
      const selected = (params.item.value as string[] | undefined) ?? []
      const labelId = `card-audit-filter-${params.item.field}`

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', paddingTop: 2 }}>
          <FormControl fullWidth sx={{ minWidth: 0 }}>
            <InputLabel id={labelId}>{label}</InputLabel>
            <Select
              labelId={labelId}
              label={label}
              multiple
              value={selected}
              onKeyDown={(event) => event.stopPropagation()}
              onChange={(event) => {
                const value = event.target.value
                applyValue({ ...params.item, value: typeof value === 'string' ? value.split(',') : value })
              }}
              renderValue={(values) => (values.length === 0 ? '' : `${values.length} selected`)}
              sx={{
                // Theme sets `.MuiSelect-multiple` to 8px padding, which is shorter than the
                // 12.5px outlined inputs and drops the label and value below the rest of the row.
                '&& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12.5px 32px 12.5px 12.5px',
                },
              }}
            >
              {sortedOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  <Checkbox checked={selected.includes(option)} size="small" sx={{ pointerEvents: 'none', padding: 0, marginRight: 1 }} />
                  {getOptionLabel ? getOptionLabel(option) : option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selected.length > 0 ? clearButton : null}
        </Box>
      )
    },
    headerLabel: label,
  }
}

// The default `contains` filter renders its input at `size="small"`, which is shorter than
// the header filter row's other controls (see `createSelectOperator` above) and looks clipped
// alongside them. Reuse the default filter logic, just with a full-size, height-matched input.
// getGridStringOperators()[0] is always the 'contains' operator.
const containsFilterFn = getGridStringOperators()[0].getApplyFilterFn

const createTextFilterOperator = (label: string): GridFilterOperator => ({
  label,
  value: 'contains',
  getApplyFilterFn: containsFilterFn,
  InputComponent: ({ applyValue, ...params }) => {
    const timerRef = useRef<ReturnType<typeof setTimeout>>()
    const itemRef = useRef(params.item)
    itemRef.current = params.item

    return (
      <Box sx={{ width: '100%', paddingTop: 2 }}>
        <TextField
          fullWidth
          label={label}
          defaultValue={params.item.value ?? ''}
          onChange={(event) => {
            clearTimeout(timerRef.current)
            const value = event.target.value
            timerRef.current = setTimeout(() => applyValue({ ...itemRef.current, value }), 300)
          }}
          onKeyDown={(event) => event.stopPropagation()}
        />
      </Box>
    )
  },
  headerLabel: label,
})

// Same problem as the text filter above, but for the default date filter (used by the "Time"
// column): its input also renders at `size="small"`. Keep the built-in date operators (is,
// after, before, ...) and their filtering logic, just swap in a full-size, height-matched input.
const DateFilterInput = ({ applyValue, ...params }: GridFilterInputValueProps) => {
  const toInputValue = (value: unknown) => {
    if (!value) return ''
    const date = new Date(value as string | number | Date)
    if (Number.isNaN(date.getTime())) return ''
    date.setMinutes(date.getMinutes() - date.getTimezoneOffset())
    return date.toISOString().slice(0, 16)
  }

  const commit = (value: string) => {
    const date = new Date(value)
    applyValue({ ...params.item, value: value === '' || Number.isNaN(date.getTime()) ? undefined : date })
  }

  return (
    <Box sx={{ width: '100%', paddingTop: 2 }}>
      <TextField
        fullWidth
        type="datetime-local"
        defaultValue={toInputValue(params.item.value)}
        onBlur={(event) => commit(event.target.value)}
        onKeyDown={(event) => {
          event.stopPropagation()
          if (event.key === 'Enter') commit((event.target as HTMLInputElement).value)
        }}
      />
    </Box>
  )
}

const dateTimeFilterOperators: GridFilterOperator[] = getGridDateOperators(true).map((operator) =>
  operator.InputComponent ? { ...operator, InputComponent: DateFilterInput } : operator,
)

export const AUDIT_COLUMNS: GridColDef<CardAuditLog>[] = [
  {
    field: 'cardAuditType',
    headerName: 'Action',
    flex: 1,
    minWidth: 150,
    valueFormatter: (value) => formatSnakeCaseToSentence(value),
    filterOperators: [createSelectOperator('Action', Object.values(CardAuditAction), formatSnakeCaseToSentence)],
  },
  {
    field: 'oldValue',
    headerName: 'Old value',
    flex: 1,
    valueGetter: (_value, row) => getAuditValueString(row.values, 'oldValue'),
    renderCell: (params) => renderAuditValueCell(params.row.values, 'oldValue'),
    filterOperators: [createTextFilterOperator('Old value')],
  },
  {
    field: 'newValue',
    headerName: 'New value',
    flex: 1,
    valueGetter: (_value, row) => getAuditValueString(row.values, 'newValue'),
    renderCell: (params) => renderAuditValueCell(params.row.values, 'newValue'),
    filterOperators: [createTextFilterOperator('New value')],
  },
  {
    field: 'when',
    headerName: 'Time',
    flex: 1,
    minWidth: 200,
    type: 'dateTime',
    valueGetter: (_value, row) => parseAuditTimestamp(row.when),
    valueFormatter: (value) => formatAuditDate(value as Date),
    filterOperators: dateTimeFilterOperators,
  },
  { field: 'who', headerName: 'Changed by', flex: 1, filterOperators: [createTextFilterOperator('Changed by')] },
  {
    field: 'shownToCustomer',
    headerName: 'Shown to customer',
    flex: 1,
    minWidth: 150,
    valueFormatter: (value) => (value ? 'Yes' : 'No'),
    filterOperators: [createSelectOperator('Shown to customer', ['Yes', 'No'], undefined, (val) => val === 'Yes')],
  },
]
